/**
 * Shared fetch() error handling.
 *
 * Turns a failed Response into a message that names the real cause, so callers stop
 * reporting every failure as a connection problem.
 *
 * Server error bodies are not uniform, so we check both shapes we actually emit:
 *   - HttpExceptionFilter  writes { message, correlationId }
 *   - TenantClaimEnforcementMiddleware and the cookie auth events write { error }
 * Some failures carry no body at all: sending `Accept: application/json` skips the HTML
 * status-code pages (see ShouldUseHtmlStatusCodePages in Program.cs), so a rate-limit 429
 * and an antiforgery 400 arrive genuinely empty. That is why the status-aware fallback
 * below is load-bearing rather than decorative.
 */

var STATUS_MESSAGES = {
  400: "That request was rejected — refresh the page and try again.",
  401: "Your session expired — sign in again to continue.",
  403: "You do not have permission to do that.",
  404: "That is no longer available — refresh the page.",
  409: "That changed while you were working — refresh the page and try again.",
  413: "That request was too large."
};

/**
 * Pull a usable message out of a parsed JSON error body.
 * Returns null for anything that is not a non-empty string, so a stray
 * object or number can never end up rendered in a toast.
 */
function messageFromBody(body) {
  if (!body || typeof body !== "object") return null;
  var candidate = body.error || body.message;
  if (typeof candidate !== "string") return null;
  candidate = candidate.trim();
  return candidate.length > 0 ? candidate : null;
}

/**
 * Seconds to wait after a 429, from the body or the Retry-After header
 * (the global limiter in Program.cs sets the header and writes no body).
 * @returns {number|null} whole seconds, or null when unavailable/not numeric.
 */
function retryAfterSeconds(resp, body) {
  var fromBody = body && body.retryAfterSeconds;
  var seconds = typeof fromBody === "number" ? fromBody : parseInt(resp.headers.get("Retry-After") || "", 10);
  // Retry-After may also be an HTTP date, which parseInt turns into NaN — fall through to the generic message.
  return isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null;
}

/** True when a followed redirect landed us on the sign-in page instead of the endpoint. */
function redirectedToSignIn(resp) {
  return resp.redirected === true && /\/signin\b/i.test(resp.url || "");
}

/**
 * Describe a failed Response in words a user can act on.
 *
 * Prefers the server's own message, then falls back to the status code. Never returns
 * raw non-JSON body text — that would dump a whole HTML error page into the UI.
 *
 * @param {Response} resp — a Response, normally one where resp.ok is false.
 * @param {string} [fallback] — message for statuses with nothing more specific to say.
 * @returns {Promise<string>} a user-facing message.
 */
export async function describeHttpError(resp, fallback) {
  var generic = fallback || "Request failed. Try again.";
  var body = null;
  try {
    var text = await resp.text();
    if (text) body = JSON.parse(text);
  } catch (e) {
    // Empty body, or HTML rather than JSON. Either way the status is all we have.
  }

  if (resp.status === 429) {
    var seconds = retryAfterSeconds(resp, body);
    return seconds
      ? "Too many requests — try again in " + seconds + "s."
      : "Too many requests — wait a moment and try again.";
  }

  var serverMessage = messageFromBody(body);
  if (serverMessage) return serverMessage;

  if (STATUS_MESSAGES[resp.status]) return STATUS_MESSAGES[resp.status];
  if (resp.status >= 500) return "Server error (" + resp.status + ") — try again shortly.";
  if (redirectedToSignIn(resp)) return STATUS_MESSAGES[401];
  return generic;
}

/**
 * Read a JSON response, throwing a ready-to-display Error when anything went wrong.
 *
 * Checks resp.ok *and* that the body actually parses, because an expired session can
 * arrive as a followed 302 to the sign-in page — status 200, HTML body — which would
 * otherwise masquerade as a connection failure.
 *
 * The thrown Error carries `.userMessage`. Callers should show that when present and
 * keep their own connection-related message for when fetch() itself rejects, which is
 * the one case that genuinely is the network.
 *
 * @param {Response} resp
 * @param {string} [fallback] — message for failures the status does not explain.
 * @returns {Promise<any>} the parsed JSON body.
 */
export async function readJsonOrThrow(resp, fallback) {
  if (!resp.ok) {
    throw withUserMessage(await describeHttpError(resp, fallback));
  }

  if (redirectedToSignIn(resp)) {
    throw withUserMessage(STATUS_MESSAGES[401]);
  }

  try {
    return await resp.json();
  } catch (e) {
    throw withUserMessage("Unexpected response from the server — refresh the page and try again.");
  }
}

function withUserMessage(message) {
  var err = new Error(message);
  err.userMessage = message;
  return err;
}

// Expose globally for Razor views and inline scripts (same bridge as toast.js).
window.Stackora = window.Stackora || {};
window.Stackora.http = { describeHttpError: describeHttpError, readJsonOrThrow: readJsonOrThrow };
